import { useState, useEffect, useCallback } from 'react'
import { Button } from '../ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui'
import { Input } from '../ui'
import { Label } from '../ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui'
import { Popover, PopoverContent, PopoverTrigger } from '../ui'
import { Check, ChevronsUpDown, Info, X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui'
import { cn } from '../../lib/utils'
import { UserRole } from '../../types/auth'

export interface GroupOption {
  groupId: string
  name: string
  status: 'ACTIVE' | 'DEACTIVATED'
}

export interface CompanyOption {
  id: string
  company: string
  group: string
  groupId: string
}

export interface CreateUserInput {
  username: string
  email: string
  isWhitelisted: boolean
  groupIds: string[]
  companyIds: string[]
}

export interface CreateGroupOwnerInput {
  username: string
  email: string
  groupIds: string[]
}

export interface CreateSuperAdminInput {
  username: string
  email: string
}

export interface CreateComplianceOfficerInput {
  username: string
  email: string
}

interface UnifiedCreateUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId?: string
  companyId?: string
  groups?: GroupOption[]
  companies?: CompanyOption[]
  loading?: boolean
  /**
   * Override for allowed roles. When provided, this list is filtered by the context-based restrictions.
   * For example, if companyId is present, only Trader roles from allowedRoles will be shown.
   * If not provided, uses default logic: all roles when no context, limited roles when groupId/companyId present.
   */
  allowedRoles?: UserRole[]
  onCreateUser?: (input: CreateUserInput) => Promise<void>
  onCreateGroupOwner?: (input: CreateGroupOwnerInput) => Promise<void>
  onCreateSuperAdmin?: (input: CreateSuperAdminInput) => Promise<void>
  onCreateComplianceOfficer?: (input: CreateComplianceOfficerInput) => Promise<void>
  onValidationError?: (field: string, message: string) => void
}

export function UnifiedCreateUserModal({
  open,
  onOpenChange,
  groupId,
  companyId,
  groups = [],
  companies = [],
  loading = false,
  allowedRoles,
  onCreateUser,
  onCreateGroupOwner,
  onCreateSuperAdmin,
  onCreateComplianceOfficer,
  onValidationError,
}: UnifiedCreateUserModalProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [whitelisted, setWhitelisted] = useState<boolean>(true)
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(groupId ? [groupId] : [])
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(
    companyId ? [companyId] : [],
  )

  // Search state for groups and companies
  const [groupSearchValue, setGroupSearchValue] = useState('')
  const [companySearchValue, setCompanySearchValue] = useState('')
  const [groupComboboxOpen, setGroupComboboxOpen] = useState(false)
  const [companyComboboxOpen, setCompanyComboboxOpen] = useState(false)

  const [errors, setErrors] = useState<{
    role?: string
    username?: string
    email?: string
    groups?: string
    companies?: string
  }>({})

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleRoleChange = (role: UserRole) => {
    setSelectedRole(role)
    if (errors.role) {
      setErrors((prev) => ({ ...prev, role: undefined }))
    }
    // Reset form when role changes
    setUsername('')
    setEmail('')
    setWhitelisted(true)
    setSelectedGroupIds(groupId ? [groupId] : [])
    setSelectedCompanyIds(companyId ? [companyId] : [])
    setErrors({})
    setGroupSearchValue('')
    setCompanySearchValue('')
    setGroupComboboxOpen(false)
    setCompanyComboboxOpen(false)
  }

  const handleUsernameChange = (value: string) => {
    setUsername(value)
    if (errors.username) {
      setErrors((prev) => ({ ...prev, username: undefined }))
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }))
    }
  }

  const handleWhitelistChange = (value: string) => {
    setWhitelisted(value === 'yes')
  }

  // Group handling functions
  const getGroupName = (groupId: string) => {
    return groups.find((group) => group.groupId === groupId)?.name || 'Unknown Group'
  }

  const handleGroupSelect = (groupId: string) => {
    if (!selectedGroupIds.includes(groupId)) {
      setSelectedGroupIds((prev) => [...prev, groupId])
      if (errors.groups) {
        setErrors((prev) => ({ ...prev, groups: undefined }))
      }
    }
  }

  const handleGroupRemove = (groupIdToRemove: string) => {
    // Don't allow removing the pre-selected group
    if (groupId && groupIdToRemove === groupId) return
    setSelectedGroupIds((prev) => prev.filter((id) => id !== groupIdToRemove))

    // Remove all companies that belong to the removed group
    setSelectedCompanyIds((prev) =>
      prev.filter((companyId) => {
        const company = companies.find((c) => c.id === companyId)
        return company && company.groupId !== groupIdToRemove
      }),
    )
  }

  // Company handling functions
  const getCompanyName = (companyId: string) => {
    return companies.find((company) => company.id === companyId)?.company || 'Unknown Company'
  }

  const handleCompanySelect = (companyId: string) => {
    if (companyId === 'SELECT_ALL') {
      // Select all available companies from selected groups
      const companiesForSelectedGroups =
        selectedGroupIds.length > 0
          ? companies.filter((company) => selectedGroupIds.includes(company.groupId))
          : []
      const allAvailableCompanyIds = companiesForSelectedGroups.map((company) => company.id)
      setSelectedCompanyIds(allAvailableCompanyIds)
      if (errors.companies) {
        setErrors((prev) => ({ ...prev, companies: undefined }))
      }
    } else if (!selectedCompanyIds.includes(companyId)) {
      setSelectedCompanyIds((prev) => [...prev, companyId])
      if (errors.companies) {
        setErrors((prev) => ({ ...prev, companies: undefined }))
      }
    }
  }

  const handleCompanyRemove = (companyIdToRemove: string) => {
    // Don't allow removing the pre-selected company
    if (companyId && companyIdToRemove === companyId) return
    setSelectedCompanyIds((prev) => prev.filter((id) => id !== companyIdToRemove))
  }

  const availableGroups = groups.filter((group) => !selectedGroupIds.includes(group.groupId))

  // Filter companies to only show those from selected groups
  const companiesForSelectedGroups =
    selectedGroupIds.length > 0
      ? companies.filter((company) => selectedGroupIds.includes(company.groupId))
      : []

  const availableCompanies = companiesForSelectedGroups.filter(
    (company) => !selectedCompanyIds.includes(company.id),
  )

  const resetForm = () => {
    setSelectedRole('')
    setUsername('')
    setEmail('')
    setWhitelisted(true)
    setSelectedGroupIds(groupId ? [groupId] : [])
    setSelectedCompanyIds(companyId ? [companyId] : [])
    setErrors({})
    setGroupSearchValue('')
    setCompanySearchValue('')
    setGroupComboboxOpen(false)
    setCompanyComboboxOpen(false)
  }

  const getAvailableRoles = useCallback((): UserRole[] => {
    // Determine base roles based on context
    let baseRoles: UserRole[]

    // When company ID is present, only show Trader role
    if (companyId) {
      baseRoles = [UserRole.Trader]
    }
    // When group ID is present, don't show Super Admin and Compliance
    else if (groupId) {
      baseRoles = [UserRole.Trader, UserRole.GroupOwner]
    }
    // No restrictions - show all roles
    else {
      baseRoles = [UserRole.Trader, UserRole.GroupOwner, UserRole.SuperAdmin, UserRole.Compliance]
    }

    // If allowedRoles prop is provided, filter it by the base roles
    if (allowedRoles && allowedRoles.length > 0) {
      return allowedRoles.filter((role) => baseRoles.includes(role))
    }

    // Return base roles when no override is provided
    return baseRoles
  }, [companyId, groupId, allowedRoles])

  // Reset selected role if it's no longer available when props change
  useEffect(() => {
    const availableRoles = getAvailableRoles()
    if (selectedRole && !availableRoles.includes(selectedRole)) {
      setSelectedRole('')
      setUsername('')
      setEmail('')
      setErrors({})
      setGroupSearchValue('')
      setCompanySearchValue('')
      setGroupComboboxOpen(false)
      setCompanyComboboxOpen(false)
    }
  }, [groupId, companyId, allowedRoles, selectedRole, getAvailableRoles])

  const handleValidationError = (field: string, message: string) => {
    if (onValidationError) {
      onValidationError(field, message)
    }
    setErrors((prev) => ({ ...prev, [field]: message }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Reset errors
    setErrors({})

    // Validation
    const newErrors: typeof errors = {}

    if (!selectedRole) {
      newErrors.role = 'Please select a role'
    }

    if (!username.trim()) {
      newErrors.username = 'Username is required'
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email'
    }

    // Role-specific validation
    if (selectedRole === UserRole.Trader) {
      if (selectedGroupIds.length === 0) {
        newErrors.groups = 'Please choose at least 1 group'
      }
      if (selectedCompanyIds.length === 0) {
        newErrors.companies = 'Please choose at least 1 company'
      }
    } else if (selectedRole === UserRole.GroupOwner) {
      if (selectedGroupIds.length === 0) {
        newErrors.groups = 'Please choose at least 1 group'
      }
    }
    // SuperAdmin and Compliance don't need additional validation

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      if (selectedRole === UserRole.Trader && onCreateUser) {
        await onCreateUser({
          username: username.trim(),
          email: email.trim(),
          isWhitelisted: whitelisted,
          groupIds: selectedGroupIds,
          companyIds: selectedCompanyIds,
        })
        resetForm()
        onOpenChange(false)
      } else if (selectedRole === UserRole.GroupOwner && onCreateGroupOwner) {
        await onCreateGroupOwner({
          username: username.trim(),
          email: email.trim(),
          groupIds: selectedGroupIds,
        })
        resetForm()
        onOpenChange(false)
      } else if (selectedRole === UserRole.SuperAdmin && onCreateSuperAdmin) {
        await onCreateSuperAdmin({
          username: username.trim(),
          email: email.trim(),
        })
        resetForm()
        onOpenChange(false)
      } else if (selectedRole === UserRole.Compliance && onCreateComplianceOfficer) {
        await onCreateComplianceOfficer({
          username: username.trim(),
          email: email.trim(),
        })
        resetForm()
        onOpenChange(false)
      }
    } catch (error) {
      // Error handling is done by parent component
      if (error instanceof Error) {
        const errorMessage = error.message

        // Handle specific validation errors
        if (errorMessage.includes('username is already taken')) {
          handleValidationError('username', 'This username is already taken')
        } else if (errorMessage.includes('email is already taken')) {
          handleValidationError('email', 'This email is already taken')
        } else if (errorMessage.includes('valid email')) {
          handleValidationError('email', 'Please enter a valid email')
        } else if (errorMessage.includes('choose at least 1 group')) {
          handleValidationError('groups', 'Please choose at least 1 group')
        } else if (errorMessage.includes('choose at least 1 company')) {
          handleValidationError('companies', 'Please choose at least 1 company')
        }
      }
    }
  }

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRole.Trader:
        return 'Trader'
      case UserRole.GroupOwner:
        return 'Group Owner'
      case UserRole.SuperAdmin:
        return 'Super Admin'
      case UserRole.Compliance:
        return 'Compliance Officer'
      default:
        return role
    }
  }

  const getModalTitle = () => {
    if (!selectedRole) return 'Create User'
    return `Create New ${getRoleDisplayName(selectedRole)}`
  }

  const isFormValid = () => {
    if (!selectedRole || !username || !email) return false

    if (selectedRole === UserRole.Trader) {
      return selectedGroupIds.length > 0 && selectedCompanyIds.length > 0
    } else if (selectedRole === UserRole.GroupOwner) {
      return selectedGroupIds.length > 0
    } else if (selectedRole === UserRole.SuperAdmin || selectedRole === UserRole.Compliance) {
      return true
    }

    return false
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{getModalTitle()}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role">Role *</Label>
            <Select value={selectedRole} onValueChange={handleRoleChange} disabled={loading}>
              <SelectTrigger>
                <SelectValue placeholder="Select user role" />
              </SelectTrigger>
              <SelectContent>
                {getAvailableRoles().map((role) => (
                  <SelectItem key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-sm text-red-500">{errors.role}</p>}
          </div>

          {/* Show form fields only after role is selected, in correct order */}
          {selectedRole && (
            <>
              {/* User-specific whitelist field (first for users) */}
              {selectedRole === UserRole.Trader && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="whitelist">Authorize user?</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Authorized traders can execute swaps on their own. Non-authorized traders
                          need an approval to execute swaps.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select
                    onValueChange={handleWhitelistChange}
                    value={whitelisted ? 'yes' : 'no'}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="yes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">yes</SelectItem>
                      <SelectItem value="no">no</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="Enter username"
                  className={errors.username ? 'border-red-500 focus:border-red-500' : ''}
                  disabled={loading}
                />
                {errors.username && <p className="text-sm text-red-500">{errors.username}</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="Enter email"
                  className={errors.email ? 'border-red-500 focus:border-red-500' : ''}
                  disabled={loading}
                />
                {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
              </div>

              {/* Groups (for User and GroupOwner) */}
              {(selectedRole === UserRole.Trader || selectedRole === UserRole.GroupOwner) && (
                <div className="space-y-2">
                  <Label htmlFor="groups">
                    {selectedRole === UserRole.Trader
                      ? 'Assign to group(s) *'
                      : 'Assign to group(s)'}
                  </Label>
                  <div className="space-y-2">
                    {selectedGroupIds.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedGroupIds.map((selectedGroupId) => (
                          <div
                            key={selectedGroupId}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-main/10 text-main rounded text-sm hover:translate-y-[-2px] transition-all duration-150 ease-in-out cursor-pointer"
                          >
                            {getGroupName(selectedGroupId)}
                            {!groupId || selectedGroupId !== groupId ? (
                              <button
                                type="button"
                                onClick={() => handleGroupRemove(selectedGroupId)}
                                className="text-main hover:text-main"
                                disabled={loading}
                              >
                                <X size={14} />
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    <Popover open={groupComboboxOpen} onOpenChange={setGroupComboboxOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={groupComboboxOpen}
                          className={cn(
                            'justify-between',
                            errors.groups ? 'border-red-500 focus:border-red-500' : '',
                            groupId ? 'opacity-50 cursor-not-allowed' : '',
                          )}
                          disabled={!!groupId || loading}
                        >
                          {groupId
                            ? groups.find((group) => group.groupId === groupId)?.name
                            : selectedRole === UserRole.Trader
                              ? 'Select groups'
                              : '-'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search groups..."
                            value={groupSearchValue}
                            onValueChange={setGroupSearchValue}
                          />
                          <CommandList>
                            <CommandEmpty>No groups found.</CommandEmpty>
                            <CommandGroup>
                              {availableGroups
                                .filter((group) =>
                                  group.name.toLowerCase().includes(groupSearchValue.toLowerCase()),
                                )
                                .map((group) => (
                                  <CommandItem
                                    key={group.groupId}
                                    value={group.name}
                                    onSelect={() => {
                                      handleGroupSelect(group.groupId)
                                      setGroupComboboxOpen(false)
                                    }}
                                  >
                                    <Check className="mr-2 h-4 w-4 opacity-0" />
                                    <div>
                                      <div className="font-medium">{group.name}</div>
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {errors.groups && <p className="text-sm text-red-500">{errors.groups}</p>}
                </div>
              )}

              {/* Companies (only for User) */}
              {selectedRole === UserRole.Trader && (
                <div className="space-y-2">
                  <Label htmlFor="companies">Assign to company(ies) *</Label>
                  <div className="space-y-2">
                    {selectedCompanyIds.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedCompanyIds.map((selectedCompanyId) => (
                          <div
                            key={selectedCompanyId}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm"
                          >
                            {getCompanyName(selectedCompanyId)}
                            {!companyId || selectedCompanyId !== companyId ? (
                              <button
                                type="button"
                                onClick={() => handleCompanyRemove(selectedCompanyId)}
                                className="text-green-600 hover:text-green-800"
                                disabled={loading}
                              >
                                <X size={14} />
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    <Popover open={companyComboboxOpen} onOpenChange={setCompanyComboboxOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={companyComboboxOpen}
                          className={cn(
                            'justify-between',
                            errors.companies ? 'border-red-500 focus:border-red-500' : '',
                            companyId || selectedGroupIds.length === 0
                              ? 'opacity-50 cursor-not-allowed'
                              : '',
                          )}
                          disabled={!!companyId || loading || selectedGroupIds.length === 0}
                        >
                          {companyId
                            ? companies.find((company) => company.id === companyId)?.company
                            : selectedGroupIds.length === 0
                              ? 'Select group(s) first'
                              : 'Select companies'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search companies..."
                            value={companySearchValue}
                            onValueChange={setCompanySearchValue}
                          />
                          <CommandList>
                            <CommandEmpty>No companies found.</CommandEmpty>
                            <CommandGroup>
                              {availableCompanies.length > 1 && (
                                <CommandItem
                                  value="SELECT_ALL"
                                  onSelect={() => {
                                    handleCompanySelect('SELECT_ALL')
                                    setCompanyComboboxOpen(false)
                                  }}
                                >
                                  <Check className="mr-2 h-4 w-4 opacity-0" />
                                  <div className="font-semibold">Select all</div>
                                </CommandItem>
                              )}
                              {availableCompanies
                                .filter((company) =>
                                  company.company
                                    .toLowerCase()
                                    .includes(companySearchValue.toLowerCase()),
                                )
                                .map((company) => (
                                  <CommandItem
                                    key={company.id}
                                    value={company.company}
                                    onSelect={() => {
                                      handleCompanySelect(company.id)
                                      setCompanyComboboxOpen(false)
                                    }}
                                  >
                                    <Check className="mr-2 h-4 w-4 opacity-0" />
                                    <div className="font-medium">{company.company}</div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {errors.companies && <p className="text-sm text-red-500">{errors.companies}</p>}
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end">
            <Button
              variant="main_action"
              className="w-full rounded-md"
              type="submit"
              disabled={!isFormValid() || loading}
            >
              {loading ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
